import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import i18n from "../../../../../i18n";
import FlaggedEventsIcon from "../../../../../images/reports/flaggedEventsIcon.svg?react";
import NotesIcon from "../../../../../images/reports/notesIcon.svg?react";
import LiveNotesIcon from "../../../../../images/reports/liveNotesIcon.svg?react";
import RoomScanIcon from "../../../../../images/reports/roomScanIcon.svg?react";
import IdIcon from "../../../../../images/reports/idIcon.svg?react";
import WebcamIcon from "../../../../../images/reports/webcamIcon.svg?react";
import MicroPhoneIcon from "../../../../../images/reports/microphone.svg?react";
import MonitorIcon from "../../../../../images/reports/monitor.svg?react";
import ConfigIcon from "../../../../../images/reports/configIcon.svg?react";
import ScratchNoteIcon from "../images/scratch-notes.svg?react";
import SignatureIcon from "../../../../../images/reports/signatureIcon.svg?react";
import { LiveProctoringSessionNote } from "./useVideoEvents";
import { AutomatedProctoringSessionNote } from "./useSessionNotes";
import { ProctoringAssessmentConfigurationV1 } from "@verity/proctoring-option/types";

const SECONDARY_TAB_KEYS = ["flaggedEvents", "notes", "liveNotes"] as const;
const MAIN_TAB_KEYS = ["recording", "roomScan", "config", "idPhotos", "signature", "scratchNote"] as const;
type SecondaryTab = (typeof SECONDARY_TAB_KEYS)[number];
type MainTab = (typeof MAIN_TAB_KEYS)[number];

interface TabType {
	key: string;
	label: string;
	icon?: React.JSX.Element;
	id?: string;
}

type UseResultTabsProps = {
	sessionNotes: AutomatedProctoringSessionNote[];
	visibleLiveNotes: LiveProctoringSessionNote[];
	proctoringOptions?: Partial<ProctoringAssessmentConfigurationV1>;
	hasTypedSignature?: boolean;
};

export default function useResultTabs({
	sessionNotes,
	visibleLiveNotes,
	proctoringOptions,
	hasTypedSignature,
}: UseResultTabsProps) {
	const [secondaryTab, setSecondaryTab] = useState<SecondaryTab>("flaggedEvents");
	const [mainTab, setMainTab] = useState<MainTab>("recording");
	const [mainTabOverflow, setMainTabOverflow] = useState<boolean>(false);

	const baseMainTabId = useId();
	const baseSecondaryTabId = useId();

	const mainTabsRef = useRef<HTMLDivElement | null>(null);

	// Manually calculating overflow instead of setting scrollButtons = "auto" because "auto" always showed scroll buttons even if no overflow
	useLayoutEffect(() => {
		const el = mainTabsRef.current;
		if (!el) return;

		const scroller = el.querySelector<HTMLElement>(".MuiTabs-scroller");
		const flexContainer = el.querySelector<HTMLElement>(".MuiTabs-flexContainer");

		if (!scroller || !flexContainer) {
			setMainTabOverflow(false);
			return;
		}

		const checkOverflow = () => {
			setMainTabOverflow(flexContainer.scrollWidth > scroller.clientWidth);
		};

		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(flexContainer);
		resizeObserver.observe(scroller);
		window.addEventListener("resize", checkOverflow);

		return () => {
			window.removeEventListener("resize", checkOverflow);
			resizeObserver.disconnect();
		};
	}, [mainTab]);

	const mainTabIdRecord = useMemo((): Partial<Record<MainTab, string>> => {
		const entries = MAIN_TAB_KEYS.map((category) => [category, `${baseMainTabId}-${category}`] as const);
		return Object.fromEntries(entries);
	}, [baseMainTabId]);

	const secondaryTabIdRecord = useMemo((): Partial<Record<SecondaryTab, string>> => {
		const entries = SECONDARY_TAB_KEYS.map((category) => [category, `${baseSecondaryTabId}-${category}`] as const);
		return Object.fromEntries(entries);
	}, [baseSecondaryTabId]);

	const secondaryTabList = useMemo(() => {
		const tabs: TabType[] = [
			{
				key: "flaggedEvents",
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.flaggedEvents"),
				icon: <FlaggedEventsIcon />,
				id: secondaryTabIdRecord["flaggedEvents"],
			},
		];
		if (sessionNotes.length > 0) {
			tabs.push({
				key: "notes",
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.notes"),
				icon: <NotesIcon />,
				id: secondaryTabIdRecord["notes"],
			});
		}
		if (visibleLiveNotes.length > 0) {
			tabs.push({
				key: "liveNotes",
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.liveNote"),
				icon: <LiveNotesIcon />,
				// No Id needed since it is the only "tab" on screen -> so it is not a "tab"
			});
		}
		return tabs;
	}, [secondaryTabIdRecord, sessionNotes.length, visibleLiveNotes.length]);

	const mainTabList = useMemo((): TabType[] => {
		if (!proctoringOptions) {
			return [];
		}

		const hasWebcamRecording = proctoringOptions.videoRecordingMode !== "off";
		const hasScreenRecording = proctoringOptions.recordScreen;
		const hasAudioRecording = proctoringOptions.audioRecordingMode !== "off";
		const hasScratchNote = proctoringOptions.uploadScratchNotes;
		const tabs: TabType[] = [];

		if (hasWebcamRecording && hasScreenRecording && hasAudioRecording) {
			tabs.push({
				key: "recording",
				icon: <WebcamIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcamAndScreenRecording"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasWebcamRecording && hasScreenRecording) {
			tabs.push({
				key: "recording",
				icon: <WebcamIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcamAndScreenRecording"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasWebcamRecording && hasAudioRecording) {
			tabs.push({
				key: "recording",
				icon: <WebcamIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcam"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasScreenRecording && hasAudioRecording) {
			tabs.push({
				key: "recording",
				icon: <MonitorIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.screenAndAudio"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasWebcamRecording) {
			tabs.push({
				key: "recording",
				icon: <WebcamIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.webcam"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasScreenRecording) {
			tabs.push({
				key: "recording",
				icon: <MonitorIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.screen"),
				id: mainTabIdRecord["recording"],
			});
		} else if (hasAudioRecording) {
			tabs.push({
				key: "recording",
				icon: <MicroPhoneIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.audio"),
				id: mainTabIdRecord["recording"],
			});
		}

		if (proctoringOptions?.scanRoom) {
			tabs.push({
				key: "roomScan",
				icon: <RoomScanIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.roomScan"),
				id: mainTabIdRecord["roomScan"],
			});
		}
		if (proctoringOptions?.idVerification) {
			tabs.push({
				key: "idPhotos",
				icon: <IdIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.idPhotos"),
				id: mainTabIdRecord["idPhotos"],
			});
		}
		if (proctoringOptions?.uploadSignature && !hasTypedSignature) {
			tabs.push({
				key: "signature",
				icon: <SignatureIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.signature.tab"),
				id: mainTabIdRecord["signature"],
			});
		}
		if (hasScratchNote) {
			tabs.push({
				key: "scratchNote",
				icon: <ScratchNoteIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.scratchNote.tab"),
				id: mainTabIdRecord["scratchNote"],
			});
		}

		if (Object.keys(proctoringOptions).length > 0) {
			tabs.push({
				key: "config",
				icon: <ConfigIcon />,
				label: i18n.t("result.components.studentSelection.videoPlayer.videoPlayerTSX.configurations"),
				id: mainTabIdRecord["config"],
			});
		}
		return tabs;
	}, [mainTabIdRecord, proctoringOptions, hasTypedSignature]);

	const handleMainTabChange = useCallback((newTab: MainTab) => {
		setMainTab(newTab);
	}, []);

	const handleSecondaryTabChange = useCallback((newTab: SecondaryTab) => {
		setSecondaryTab(newTab);
	}, []);

	// Reset to first tab when tab list changes
	useEffect(() => {
		setMainTab((mainTabList[0]?.key as MainTab) || "recording");
	}, [mainTabList]);

	return {
		mainTab,
		secondaryTab,
		mainTabList,
		secondaryTabList,
		handleMainTabChange,
		handleSecondaryTabChange,
		mainTabOverflow,
		mainTabsRef,
		mainTabIdRecord,
		secondaryTabIdRecord,
	};
}
